import { UploadFile } from 'antd';
import { Flex, Typography, Upload, ProgressBar } from '@arkestro/arkestro-design-system';
import useTranslation from 'next-translate/useTranslation';
import PdfImg from '@shared/file-icons/pdf.svg';
import XlsxImg from '@shared/file-icons/xlsx.svg';
import CsvImg from '@shared/file-icons/csv.svg';
import GenericImg from '@shared/file-icons/generic.svg';
import UploadImg from '@shared/file-icons/document-types.svg';
import styles from './DraggerPanel.module.scss';

interface Props {
  fileList: UploadFile[];
  handleFileChange: (info: unknown) => void;
}

export const DraggerPanel = ({ fileList, handleFileChange }: Props) => {

  const { t } = useTranslation('common');
  const { Dragger } = Upload;

  const uploadProgress = fileList.length > 0 ? Math.floor((fileList[0].percent || 0)) : null;
  const fileName = fileList.length > 0 ? fileList[0].name : null;
  const fileType = fileList.length > 0 ? fileList[0].type : null;
  const uploadStatus = fileList.length > 0 ? fileList[0].status : null;

  const uploadStatuses: { [key: string]: 'error' | 'success' | undefined } = {
    'done': 'success',
    'error': 'error',
    'uploading': undefined
  };

  const description = {
    'done': t('rfx_drawer.rff.file_uploaded'),
    'error': t('rfx_drawer.rff.file_upload_failed'),
    'uploading': t('rfx_drawer.rff.file_uploading')
  };

  const imgSrc = () => {
    if (fileType === 'application/pdf') return PdfImg.src;
    if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return XlsxImg.src;
    if (fileType === 'text/csv') return CsvImg.src;
    return GenericImg.src;
  };

  return (
    <Dragger
      accept='.pdf,.xlsx,.csv,.docx,.tsv,.png,.jpg,.jpeg,.webp'
      data-testid='file-upload-dragger'
      fileList={fileList}
      onChange={handleFileChange}
      showUploadList={false}
    >
      {fileList.length === 0 &&
        <Flex align='center' className={styles.draggerPanel} gap={16} justify='center' vertical>
          <img alt='file upload' height={75} src={UploadImg.src} width={75} />
          <Flex vertical>
            <Typography className={styles.hintText}>
              {t('rfx_drawer.rff.dragger_help_text_line_1')}
            </Typography>
            <Typography className={styles.hintText}>
              {t('rfx_drawer.rff.dragger_help_text_line_2')}
            </Typography>
          </Flex>
        </Flex>
      }
      {
        (uploadStatus === 'done' || uploadStatus === 'uploading' || uploadStatus === 'error') &&
        <Flex className={styles.progressBarWrapper} justify='center'>
          <img alt='upload status' height={67} src={imgSrc()} width={67} />
          <ProgressBar
            className={styles.progressBar}
            description={description[uploadStatus]}
            label={fileName ?? ''} percent={uploadProgress ?? 0}
            status={uploadStatuses[uploadStatus]}
          />
        </Flex>
      }
    </Dragger>
  );
};
